import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Input,
  Menu,
  Modal,
  Row,
  Select,
  //Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import copy from 'copy-text-to-clipboard';
import moment from 'moment';
import {
  CardSection,
  CopyComponent,
  CopySpan,
  DataGrid,
  FormLabel,
  HeaderTitle,
  InfoButton,
  Label,
  Loading,
  ScreenMask,
  SecondaryButton,
  SpaceCell,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  LOADING_ICON_SIZE1,
  DATAGRID_CELL_ICON_WIDTH2,
} from 'constants/config';
import { deleteWarehouseBatchTransfer, fetchWarehouseBatchTransferList, submitWarehouseBatchTransfer } from 'services/inventory';
import { convertTimeByUtc, dateSorter } from 'utils';
//import { getUrl } from 'utils/authenticate';
import TransferBatchDetailDialog from './TransferBatchDetailDialog';

const WHTransferList = () => {
  const DATE_SEARCH_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
  const DEFAULT_GRID_PAGE_SIZE = 20;
  const { useRef, useState } = React;
  const [batchDetailDialogVisible, setBatchDetailDialogVisible] = useState(false);
  const [batchList, setBatchList] = useState<StringKAnyVPair[]>([]);
  const [batchStatus, setBatchStatus] = useState<number>();
  const [creator, setCreator] = useState('');
  const [dateRange, setDateRange] = useState<[any, any]>([moment().subtract(30, 'days').startOf('days'), moment().endOf('days')]);
  const [filterColSpan] = useState(8);
  const [gridCount, setGridCount] = useState(0);
  const [lockScreen, setLockScreen] = useState(false);
  const [searchNumber, setSearchNumber] = useState<string>();
  const [searchNumberType, setSearchNumberType] = useState(1);
  const [searchDateType, setSearchDateType] = useState(1);
  const [selectedWHBatchNumber, setSelectedWHBatchNumber] = useState(0);
  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(DEFAULT_GRID_PAGE_SIZE);
  const history = useHistory();

  const columns = [
    {
      header: 'Batch Number',
      name: 'batchNumber',
      render(row: any) {
        const { data } = row;

        return (
          <CopyComponent value={data.batchNumber}>
            <Typography.Text
              className="text-clickable text-blue cell-1-icon"
              ellipsis={{ tooltip: data.batchNumber }}
              onClick={() => openBatchDetailDialog(data.warehouseBatchTransferNum)}
            >
              {data.batchNumber}
            </Typography.Text>
          </CopyComponent>
        );
      },
    },
    {
      header: 'Status',
      name: 'status',
      maxWidth: 110,
      render(row: any) {
        const { data } = row;

        return renderTransferStatusCell(data);
      },
    },
    {
      header: 'Update Date',
      name: 'updateDate',
      sort: dateSorter,
      render(row: any) {
        const { data } = row;

        return convertTimeByUtc(data.updateDate);
      },
    },
    {
      header: 'Create Date',
      name: 'createDate',
      sort: dateSorter,
      render(row: any) {
        const { data } = row;

        return convertTimeByUtc(data.createDate);
      },
    },
    {
      header: 'Create By',
      name: 'createBy',
      render(row: any) {
        const { data } = row;

        return (
          <CopySpan
            inGrid
            value={data.createBy}
          />
        );
      },
    },
    {
      headerAlign: 'left',
      header: 'Action',
      maxWidth: 206,
      minWidth: 206,
      name: '',
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return renderActionCell(data);
      },
    },
  ];

  const clearBatchGrid = () => {
    setBatchList([]);
    setGridCount(0);
  };

  const closeBatchDetailDialog = () => {
    setBatchDetailDialogVisible(false);
    setSelectedWHBatchNumber(0);
  };

  const deleteBatch = (batch: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: `Are you sure to delete this transfer(${batch.batchNumber})?`,
      title: 'Confirm',
      onOk: async () => {
        const idx = batchList.findIndex(e => e.warehouseBatchTransferNum === batch.warehouseBatchTransferNum);

        if (idx > -1) {
          setLockScreen(true);
          try {
            const res = await deleteWarehouseBatchTransfer(batch.warehouseBatchTransferNum);

            //console.log('r', res);
            if (res) {
              //batchList.splice(idx, 1);
              batch.status = res.status;
              batch.updateBy = res.updateBy;
              batch.updateDate = res.updateDate;
              setBatchList([ ...batchList ]);
              message.success({
                content: 'Deleted successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } catch(e) {
            message.error({
              content: `Deleted error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            setLockScreen(false);
          }
        }
      },
    });
  };

  const getTransferRowClass = (row: any) => {
    const { data } = row;

    if (data.status === 0 && data.error) return 'bg-error';
  };

  const loadTransferList = async () => {
    setLockScreen(true);
    try {
      const params: StringKAnyVPair = {
        $skip: gridPageIndex.current * gridPageSize.current,
        $sortBy: 'batchNum desc',
        $top: gridPageSize.current,
        status: batchStatus,
      };
      const sdRange = `${dateRange[0].startOf('days').format(DATE_SEARCH_FORMAT)}@${dateRange[1].endOf('days').format(DATE_SEARCH_FORMAT)}`;
      const snKey = typeof searchNumber === 'string' ? searchNumber.trim() : '';

      if (snKey !== '') {
        if (searchNumberType === 1) {
          params['BatchNumber'] = snKey
        } else {
          params['ReferenceNumber'] = snKey
        }
      }

      if (searchDateType === 1) {
        params['UpdateDate'] = sdRange;
      } else {
        params['CreateDate'] = sdRange;
      }

      if (creator.trim() !== '') params['CreateBy'] = creator.trim();

      const res = await fetchWarehouseBatchTransferList(params);

      console.log('res', params, res);
      if (res) {
        setGridCount(res.total);
        setBatchList(res.data);
      } else {
        clearBatchGrid();
      }
    } catch(e) {
    } finally {
      setLockScreen(false);
    }
  };

  const onBatchSubmitted = (tb: StringKAnyVPair) => {
    const batch = batchList.find(e => e.warehouseBatchTransferNum === tb.warehouseBatchTransferNum);

    if (batch) {
      batch.status = tb.status;
      batch.updateBy = tb.updateBy;
      batch.updateDate = tb.updateDate;
      setBatchList([ ...batchList ]);
    }
  };

  const onChangeBatchStatus = (status: any) => {
    setBatchStatus(status);
  };

  const onChangeCreator = (evt: any) => {
    setCreator(evt.target.value);
  };

  const onChangeSearchDateType = (typ: any) => {
    setSearchDateType(typ);
  };

  const onChangeSearchNumber = (evt: any) => {
    setSearchNumber(evt.target.value);
  };

  const onChangeSearchNumberType = (typ: any) => {
    setSearchNumberType(typ);
  };

  const onLimitChange = (limit: number) => {
    //gridRef?.current.deselectAll();
    if (gridPageSize.current !== limit) {
      if (gridPageIndex.current * limit >= gridCount) gridPageIndex.current = 0;

      gridPageSize.current = limit;
      loadTransferList();
    }
  };

  const onSkipChange = (skip: number) => {
    const idx = skip / gridPageSize.current;
    //gridRef?.current.deselectAll();
    //console.log('s', skip, idx);
    if (gridPageIndex.current !== idx) {
      gridPageIndex.current = idx;
      loadTransferList();
    }
  };

  const onSearchDateRangerChange = (range: any) => {
    //console.log('ch', range);
    console.log(range[0].format('YYYY-MM-DD'));
    setDateRange(range);
  };

  const openBatchDetailDialog = (wbn: number) => {
    setSelectedWHBatchNumber(wbn);
    setBatchDetailDialogVisible(true);
  };

  const renderActionCell = (data: StringKAnyVPair) => {
    return (<div className="ant-input-group-wrapper w-none">
      {data.status === 0 && (
        <SecondaryButton
          onClick={() => editTransferBatch(history, data.warehouseBatchTransferNum)}
          size="small"
        >
          <EditOutlined />
          Edit
        </SecondaryButton>
      )}
      {data.status === 0 && (
        <InfoButton
          onClick={() => submitBatch(data)}
          size="small"
        >
          <CheckOutlined />
          Submit
        </InfoButton>
      )}
      {data.status === 0 && (
        <Dropdown
          overlay={(
            <Menu>
              <Menu.Item disabled={data.status !== 0} key="rm-batch-btn" onClick={() => deleteBatch(data)}>
                <Label className="label-danger">
                  <DeleteOutlined />
                  &nbsp;Delete
                </Label>
              </Menu.Item>
            </Menu>
          )}
        >
          <Button size="small">
            <EllipsisOutlined />
          </Button>
        </Dropdown>
      )}
    </div>);
  };

  const renderFilterGroup = () => {
    return (
      <Row>
        <Col span={filterColSpan}>
          <Row align="middle">
            <div className="w-40">
              <Select
                bordered={false}
                className="form-label-selector w-100"
                onChange={onChangeSearchNumberType}
                options={[
                  {label: 'Batch Number', value: 1},
                  {label: 'Reference Number', value: 2},
                ]}
                size="small"
                value={searchNumberType}
              />
            </div>
            <div className="w-60">
              <Input
                onChange={onChangeSearchNumber}
                allowClear
                value={searchNumber}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle">
            <div className="pr-8 text-end w-40">
              <FormLabel>Status</FormLabel>
            </div>
            <div className="w-60">
              <Select
                allowClear
                className="w-100"
                onChange={onChangeBatchStatus}
                options={[
                  {label: 'Pending', value: 0},
                  {label: 'Submitted', value: 1},
                  {label: 'Processing', value: 2},
                  {label: 'Success', value: 8},
                  {label: 'Deleted', value: 9},
                ]}
                value={batchStatus}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle">
            <div className="w-40">
              <Select
                bordered={false}
                className="form-label-selector w-100"
                onChange={onChangeSearchDateType}
                options={[
                  {label: 'Update Date', value: 1},
                  {label: 'Create Date', value: 2},
                ]}
                size="small"
                value={searchDateType}
              />
            </div>
            <div className="w-60">
              <DatePicker.RangePicker
                defaultValue={dateRange}
                format="MM/DD/YYYY"
                onChange={onSearchDateRangerChange}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle" className="mt-8">
            <div className="pr-8 text-end w-40">
              <FormLabel>Create By</FormLabel>
            </div>
            <div className="w-60">
              <Input
                allowClear
                onChange={onChangeCreator}
                value={creator}
              />
            </div>
          </Row>
        </Col>
        <Col span={filterColSpan}>
          <Row align="middle" className="mt-8" justify="end">
            <Button
              onClick={loadTransferList}
              type="primary"
            >
              <SearchOutlined />
              Search
            </Button>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderTransferStatusCell = (row: StringKAnyVPair) => {
    const label = batchStatusLabel(row.status);

    return isErrorExist(row) ? (
      <SpaceCell
        icon={<>
          <Tooltip
            className="text-danger"
            placement="top"
            //title={typeof row.error === 'string' ? row.error.replace(/\n/g, '<br />') : row.error}
            title={row.error}
            trigger={['hover', 'click']}
          >
            <InfoCircleOutlined />
          </Tooltip>
          <Tooltip
            placement="top"
            title="Copy error"
            trigger={['hover', 'click']}
          >
            <CopyOutlined
              className="text-info"
              onClick={() => {
                copy(row.error);
                message.info({
                  content: 'Error has been copied to clipboard',
                  duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
                });
              }}
            />
          </Tooltip>
        </>}
        iconWidth={DATAGRID_CELL_ICON_WIDTH2}
        text={label}
      />
    ) : label;
  };

  const submitBatch = (batch: StringKAnyVPair) => {
    submitTransferBatch(
      batch,
      setLockScreen,
      (tb: StringKAnyVPair) => {
        onBatchSubmitted(tb);
        message.success({
          content: 'Submitted successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
      },
    );
  };

  return (<>
    <HeaderTitle breadcrumb={['Warehouse Transfer', 'Initiate from WMS', 'List']} />
    <CardSection>
      {renderFilterGroup()}
      <div className="mt-16">
        <DataGrid
          columns={columns}
          count={gridCount}
          dataSource={batchList}
          limit={gridPageSize.current}
          onLimitChange={onLimitChange}
          onSkipChange={onSkipChange}
          pageSizes={[5, 20, 50, 100, 200]}
          pagination
          rowKey="batchNumber"
          rowClassName={getTransferRowClass}
          style={{height: 'calc(100vh - 240px)', maxHeight: 600}}
        />
      </div>
    </CardSection>
    {batchDetailDialogVisible && (
      <TransferBatchDetailDialog
        batchNumber={selectedWHBatchNumber}
        lockScreen={setLockScreen}
        onCancel={closeBatchDetailDialog}
        onSubmit={onBatchSubmitted}
        visible={batchDetailDialogVisible}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default WHTransferList;

export const batchStatusLabel = (st: number) => {
  let ret = '';

  switch(st) {
    case 0:
      ret = 'Pending';
      break;

    case 1:
      ret = 'Submitted';
      break;

    case 2:
      ret = 'Processing';
      break;

    case 8:
      ret = 'Success';
      break;

    case 9:
      ret = 'Deleted';
      break;
  }

  return ret;
};

export const editTransferBatch = (history: any, bn: number) => {
  const url = `/warehouse-transfer/wms/recv?id=${bn}`;

  //history.push(getUrl(url));
  history.push(url);
};

export const isErrorExist = (tb: StringKAnyVPair) => {
  return tb.status === 0 && tb.error;
};

export const submitTransferBatch = async (
  item: StringKAnyVPair,
  lockScreen: Function,
  callback?: Function,
) => {
  Modal.confirm({
    centered: true,
    content: `Submit warehouse transfer ${item.batchNumber} and update inventory`,
    title: 'Confirm',
    onOk: async () => {
      lockScreen(true);
      try {
        const res = await submitWarehouseBatchTransfer(item.warehouseBatchTransferNum);
        //console.log('sub res', res);
        if (callback && res) callback(res);
      } catch(e) {
        message.error({
          content: `Submitted error: ${e}`,
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      } finally {
        lockScreen(false);
      }
    },
  });
};
